import { ChevronDown, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  /** Ties the control to its `<Label htmlFor>`, as the single Select it replaced did. */
  id?: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: MultiSelectOption[];
  /** Shown when nothing is selected — the "no filter" wording, e.g. "Any status". */
  placeholder: string;
  className?: string;
}

/**
 * Adds or removes one value, returning a new array.
 *
 * Returns a copy rather than mutating: the current selection comes from the URL
 * via the router, and mutating it in place would edit state React is holding.
 * Appending keeps the order values were chosen in, which is the order that
 * reaches the URL — so a link keeps matching the screen it was copied from.
 */
export function toggleValue(selected: string[], value: string): string[] {
  return selected.includes(value)
    ? selected.filter((entry) => entry !== value)
    : [...selected, value];
}

/**
 * What the trigger reads when a selection is applied.
 *
 * Names one or two selections in full and counts beyond that: three of the
 * user list's seven statuses already overflow the trigger at the width the
 * filter bar gives it, and a truncated label is less use than a count.
 *
 * Values with no matching option are left out rather than shown raw — a
 * hand-edited URL can name something the list no longer offers, and the router
 * drops those before they reach a request anyway.
 */
export function summaryLabel(
  selected: string[],
  options: MultiSelectOption[],
  placeholder: string,
): string {
  const labels = selected
    .map((value) => options.find((option) => option.value === value)?.label)
    .filter((label): label is string => label !== undefined);

  if (labels.length === 0) return placeholder;
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.length} selected`;
}

/**
 * A dropdown that filters on several values at once.
 *
 * A checkbox list in a popover rather than a native `<select multiple>`: the
 * native control needs modifier-clicking to build a selection, which is not
 * discoverable and does not work on touch at all.
 *
 * The empty selection is the "no filter" state and is what the caller should
 * send as absent — this component does not distinguish "nothing selected" from
 * "everything selected", because the lists behind it do not either.
 */
export function MultiSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  className,
}: MultiSelectProps) {
  const summary = summaryLabel(value, options, placeholder);
  const hasSelection = value.length > 0;

  return (
    // Relative so the clear control can sit inside the trigger's box without
    // being inside the trigger's <button>. Nesting a button in a button is
    // invalid HTML, and assistive technology reports only the outer one — so
    // the clear became unreachable to exactly the readers who most need it.
    <div className={cn('relative', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-describedby={id ? `${id}-count` : undefined}
            // Right padding leaves room for the chevron and, when there is a
            // selection, the clear control laid over it.
            className={cn(
              'w-full justify-between overflow-hidden pr-14 font-normal',
              !hasSelection && 'text-muted-foreground',
            )}
          >
            <span className="truncate">{summary}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-h-[300px] w-[220px] overflow-y-auto p-0" align="start">
          <div className="p-2" role="listbox" aria-multiselectable="true">
            {options.map((option) => {
              const checked = value.includes(option.value);
              const handleToggle = () => onChange(toggleValue(value, option.value));

              return (
                <div
                  key={option.value}
                  role="option"
                  tabIndex={0}
                  aria-selected={checked}
                  className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-gray-100"
                  onClick={handleToggle}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleToggle();
                    }
                  }}
                >
                  <Checkbox checked={checked} tabIndex={-1} />
                  <span className="text-sm">{option.label}</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Announced rather than drawn: the trigger shows a count once the labels
          stop fitting, but "3 selected" alone does not say how much of the list
          that is. */}
      {id && (
        <span id={`${id}-count`} className="sr-only">
          {`${value.length} of ${options.length} selected`}
        </span>
      )}

      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        <ChevronDown className="h-4 w-4 opacity-50" />
      </span>

      {hasSelection && (
        <button
          type="button"
          aria-label={`Clear ${placeholder.toLowerCase()} filter`}
          className="absolute inset-y-0 right-8 my-auto h-5 w-5 rounded-sm opacity-50 hover:bg-gray-100 hover:opacity-100"
          onClick={() => onChange([])}
        >
          <X className="mx-auto h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
